import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Container queries, done with what this Tailwind build actually has.
 *
 * A resizable panel's content width has nothing to do with the viewport width: at 1920px with both
 * side panels open the Testing chat column is ~660px, and at 1280px it is ~400px — yet a `sm:` class
 * (viewport ≥ 640px) is active in both. Styling panel internals off viewport breakpoints is why the
 * plan-preview table and chip grid rendered three columns inside a 400px column.
 *
 * Tailwind 3.4 has no `@container` support and the container-queries plugin is not installed, so this
 * hook measures the element and exposes the crossed stops as bare `data-cw-*` attributes. Combine it
 * with a named group to style descendants:
 *
 *   const { ref, attrs } = useContainerBreakpoint(CHAT_COLUMN_STOPS);
 *   <div ref={ref} {...attrs} className="group/chat">…</div>
 *   // deep inside:
 *   <div className="grid grid-cols-1 group-data-[cw-md]/chat:grid-cols-2 group-data-[cw-lg]/chat:grid-cols-3" />
 *
 * Stops must be a module-level constant so the memo dependency is stable.
 */
export function useContainerBreakpoint<T extends HTMLElement>(stops: Record<string, number>) {
	const ref = useRef<T>(null);
	const [width, setWidth] = useState(0);

	useEffect(() => {
		const el = ref.current;
		if (!el || typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver((entries) => {
			const next = Math.round(entries[0]?.contentRect.width ?? 0);
			setWidth((prev) => (prev === next ? prev : next));
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const attrs = useMemo(() => {
		const out: Record<string, string> = {};
		for (const [name, min] of Object.entries(stops)) {
			if (width >= min) out[`data-cw-${name}`] = "";
		}
		return out;
	}, [width, stops]);

	return { ref, width, attrs };
}

/**
 * Stops for the Testing chat column, chosen from what its content needs rather than from the viewport
 * scale: `md` is where a two-up chip grid and the plan table's secondary columns start to read, `lg`
 * is where three-up does.
 */
export const CHAT_COLUMN_STOPS = { md: 480, lg: 640 } as const;
